import React from 'react'
import { Roles } from '@/core/enums/roles'
import { TipoUsuario } from '@/core/enums/tipo-usuario'
import {
  BaseEnum,
  Funcionalidade,
  GetUsuarioLogadoRolesQueryQuery,
  GetUsuarioLogadoRolesQueryQueryVariables,
  type Maybe
} from '@/core/types/graphql'
import { getUsuarioLogadoRolesQueryGql } from '../data/usuario-logado-roles'
import { useQuery } from '@apollo/client'

// Função para verificar se as funcionalidades incluem os roles permitidos
export const checkIfIsAllowed = (
  funcionalidades?: Funcionalidade[],
  roles?: Roles[]
): boolean => {
  if (!roles || roles.length === 0) {
    return true // Se não há roles, o acesso é permitido por padrão
  }

  for (const role of roles) {
    const allow = funcionalidades?.some(({ chave }) => chave === role)
    if (allow) {
      return true // Permissão concedida caso encontre um match
    }
  }

  return false // Caso contrário, não há permissão
}

// Função para verificar se o tipo do usuário está permitido
export const checkIfUserTypeIsAllowed = (
  loggedUserType?: Maybe<Partial<BaseEnum>>,
  allowedUserTypes?: TipoUsuario[]
): boolean => {
  if (!allowedUserTypes || allowedUserTypes?.length === 0) {
    return true // Se não há tipos restritos, acesso permitido
  }
  // Se não for informado um tipo, nega a permissão
  if (!loggedUserType) return false

  return allowedUserTypes.some(ut => ut === loggedUserType.id)
}

export const podeAuditar = (usuarioLogado, role) => {
  const isTecsinapse = checkIfUserTypeIsAllowed(usuarioLogado.tipo, [
    TipoUsuario.TECSINAPSE
  ])
  const hasRole = checkIfIsAllowed(usuarioLogado.funcionalidades, [role])
  return isTecsinapse || hasRole
}

// Componente AccessControl atualizado
export const AccessControl: React.FC<{
  allowedRoles?: Roles[] // Agora tipado como array do enum Roles
  allowedUserTypes?: TipoUsuario[] // Tipos permitidos do usuário
  renderNoAccess?: React.ReactNode
  children: React.ReactNode
}> = ({ allowedRoles, allowedUserTypes, renderNoAccess = null, children }) => {
  const { data } = useQuery<
    GetUsuarioLogadoRolesQueryQuery,
    GetUsuarioLogadoRolesQueryQueryVariables
  >(getUsuarioLogadoRolesQueryGql, {
    fetchPolicy: 'cache-first'
  })

  if (!data || !data.usuarioLogado) {
    return null
  }

  const { usuarioLogado } = data

  const allowed = checkIfIsAllowed(
    usuarioLogado.funcionalidades as Funcionalidade[],
    allowedRoles
  )

  const allowedUserType = checkIfUserTypeIsAllowed(
    usuarioLogado.tipo as BaseEnum,
    allowedUserTypes
  )

  if (renderNoAccess && (!allowed || !allowedUserType)) {
    return <>{renderNoAccess}</> // Renderizar quando o acesso não é permitido
  }

  if (allowed && allowedUserType) {
    return <>{children}</> // Renderizar os filhos quando permitido
  }

  return null
}
